import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";

const STATUSES = ["nuevo","pagado","intake_pendiente","en_proceso","esperando_cliente","completado","cancelado"] as const;
const STATUS_LABELS: Record<string, string> = {
  nuevo: "Pago pendiente",
  pagado: "Pagado",
  intake_pendiente: "Formulario pendiente",
  en_proceso: "En proceso",
  esperando_cliente: "Esperando cliente",
  completado: "Completado",
  cancelado: "Cancelado",
};

type Filter =
  | "todos" | "pagados" | "no_pagados" | "pago_pendiente"
  | "en_proceso" | "esperando_cliente"
  | "completados" | "cancelados" | "urgentes"
  | "form_pendiente" | "form_enviado" | "archivados"
  | "pagados_mes";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "todos", label: "Todos" },
  { id: "pago_pendiente", label: "Pago pendiente" },
  { id: "pagados", label: "Pagados" },
  { id: "pagados_mes", label: "Pagados (mes)" },
  { id: "form_pendiente", label: "Form pendiente" },
  { id: "form_enviado", label: "Form enviado" },
  { id: "en_proceso", label: "En proceso" },
  { id: "esperando_cliente", label: "Esperando cliente" },
  { id: "urgentes", label: "Urgentes" },
  { id: "completados", label: "Completados" },
  { id: "cancelados", label: "Cancelados" },
  { id: "archivados", label: "Archivados" },
];

const VALID_FILTERS = FILTERS.map((f) => f.id) as [Filter, ...Filter[]];

const searchSchema = z.object({
  filter: fallback(z.enum(VALID_FILTERS), "todos").default("todos"),
});

export const Route = createFileRoute("/admin/pedidos/")({
  validateSearch: zodValidator(searchSchema),
  component: Pedidos,
});

function Pedidos() {
  const searchParams = Route.useSearch();
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [filter, setFilter] = useState<Filter>(searchParams.filter as Filter);
  const [search, setSearch] = useState("");

  // Si cambia el query param (e.g. al venir desde un KPI del dashboard), sincroniza el filtro local
  useEffect(() => {
    setFilter(searchParams.filter as Filter);
  }, [searchParams.filter]);

  async function load() {
    const { data } = await supabase
      .from("orders")
      .select("id,status,total,created_at,updated_at,paid_at,intake_completed,customer_id,operational_answers,stripe_session_id,urgent_manual,archived,customers(full_name,email,phone,company_name,user_id),order_items(name_snapshot,item_type)")
      .order("created_at", { ascending: false })
      .limit(500);
    setRows(data ?? []);
  }
  useEffect(() => { load(); }, []);

  async function update(id: string, status: string) {
    const { error } = await supabase.from("orders").update({ status: status as any }).eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Estado actualizado"); load(); }
  }

  const filtered = useMemo(() => {
    let r = rows;
    if (filter !== "archivados") {
      r = r.filter((x) => !x.archived);
    }
    const startMonth = new Date();
    startMonth.setDate(1); startMonth.setHours(0,0,0,0);
    switch (filter) {
      case "pagados": r = r.filter((x) => x.status === "pagado"); break;
      case "pagados_mes": r = r.filter((x) => x.paid_at && new Date(x.paid_at) >= startMonth); break;
      case "no_pagados":
      case "pago_pendiente": r = r.filter((x) => x.status === "nuevo"); break;
      case "en_proceso": r = r.filter((x) => x.status === "en_proceso" || x.status === "intake_pendiente"); break;
      case "esperando_cliente": r = r.filter((x) => x.status === "esperando_cliente"); break;
      case "completados": r = r.filter((x) => x.status === "completado"); break;
      case "cancelados": r = r.filter((x) => x.status === "cancelado"); break;
      case "urgentes": r = r.filter((x) => x.urgent_manual === true || x.operational_answers?.urgent === "true"); break;
      case "form_pendiente": r = r.filter((x) => x.paid_at && !x.intake_completed && x.status !== "cancelado"); break;
      case "form_enviado": r = r.filter((x) => x.intake_completed); break;
      case "archivados": r = r.filter((x) => x.archived); break;
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter((x) =>
        (x.customers?.full_name ?? "").toLowerCase().includes(q) ||
        (x.customers?.email ?? "").toLowerCase().includes(q) ||
        (x.customers?.phone ?? "").toLowerCase().includes(q) ||
        (x.customers?.company_name ?? "").toLowerCase().includes(q) ||
        (x.id ?? "").toLowerCase().includes(q) ||
        (x.stripe_session_id ?? "").toLowerCase().includes(q)
      );
    }
    return r;
  }, [rows, filter, search]);

  function exportCsv() {
    const head = "id,status,total,created_at,paid_at,name,email,phone,company,stripe_session_id,intake_completed\n";
    const body = filtered.map((r) => [
      r.id, r.status, r.total, r.created_at, r.paid_at ?? "",
      r.customers?.full_name ?? "", r.customers?.email ?? "", r.customers?.phone ?? "", r.customers?.company_name ?? "",
      r.stripe_session_id ?? "", r.intake_completed ? "yes" : "no",
    ].map((v) => `"${String(v ?? "").replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([head + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "pedidos.csv"; a.click();
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-xl font-semibold text-foreground">Pedidos</h2>
        <Button size="sm" onClick={exportCsv} className="bg-primary text-primary-foreground hover:bg-primary/90">Exportar CSV</Button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`rounded-md border px-3 py-1.5 text-xs ${filter === f.id ? "border-primary bg-primary/10 text-primary" : "border-hairline bg-surface text-muted-foreground hover:text-foreground"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Input
          placeholder="Buscar por nombre, email, teléfono, compañía, ID o Stripe SID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xl bg-surface border-hairline"
        />
        <span className="text-xs text-muted-foreground">{filtered.length} resultados</span>
      </div>

      <div className="mt-4 overflow-x-auto rounded-md border border-hairline">
        <table className="w-full text-sm">
          <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="p-3">Fecha</th>
              <th className="p-3">ID</th>
              <th className="p-3">Cliente</th>
              <th className="p-3">Items</th>
              <th className="p-3">Total</th>
              <th className="p-3">Form</th>
              <th className="p-3">Pago</th>
              <th className="p-3">Estado</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const isUrgent = r.urgent_manual === true || r.operational_answers?.urgent === "true";
              const hasAccount = !!r.customers?.user_id;
              const items = (r.order_items ?? []).map((it: any) => it.name_snapshot).slice(0, 3).join(", ") || "—";
              const more = (r.order_items ?? []).length > 3 ? ` +${(r.order_items ?? []).length - 3}` : "";
              return (
                <tr
                  key={r.id}
                  onClick={() => navigate({ to: "/admin/pedidos/$id", params: { id: r.id } })}
                  className="cursor-pointer border-t border-hairline align-top transition-colors hover:bg-surface-2"
                >
                  <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}<br /><span className="text-[10px]">{new Date(r.created_at).toLocaleTimeString()}</span></td>
                  <td className="p-3 text-xs font-mono text-muted-foreground" title={r.id}>{r.id.slice(0, 8)}</td>
                  <td className="p-3 text-foreground">
                    <div className="flex items-center gap-2">
                      <span>{r.customers?.full_name}</span>
                      {hasAccount && <span title="Cliente registrado" className="text-xs">👤</span>}
                      {isUrgent && <span className="rounded bg-primary/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">Urgente</span>}
                      {r.archived && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">Archivado</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{r.customers?.email}</div>
                    {r.customers?.company_name && <div className="text-[10px] text-muted-foreground">🏢 {r.customers.company_name}</div>}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground max-w-[240px]">{items}{more}</td>
                  <td className="p-3 text-foreground whitespace-nowrap">{money(r.total)}</td>
                  <td className="p-3 text-xs">{r.intake_completed ? "✓" : "—"}</td>
                  <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                    {r.paid_at ? new Date(r.paid_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <select value={r.status} onChange={(e) => update(r.id, e.target.value)} className="rounded-md border border-hairline bg-surface px-2 py-1 text-xs">
                      {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] ?? s}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!r.id) { toast.error("Pedido sin ID"); return; }
                        navigate({ to: "/admin/pedidos/$id", params: { id: r.id } });
                      }}
                      className="cursor-pointer inline-flex items-center rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20"
                    >
                      Ver →
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-sm text-muted-foreground">Sin pedidos.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
